import { Modal, SvgEConsent, Tooltip } from '@ui-kit'
import { GENERATE_CONSENT_NAMESPACES, NAMESPACES } from '../../utils/constants'
import { useEffect, useState } from 'react'
import { CHECK_ELIGIBILITY_LOCATION } from '../../utils/maxRTE'
import { getEntityDataByIdEmbedded } from '../../apis/entityData.api'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import { useParams, useSearchParams } from 'react-router-dom'
import { AddDataModal } from '../MultiLookupRowRender'
import GenerateEConsentModalContent from './GenerateEConsentModalContent'
import {
  addToaster,
  getEconsentButtonStatus,
  hideLoader,
  showLoader,
} from '@app-store'
import { v4 as uuid } from 'uuid'
import { FieldValues, UseFormReturn } from 'react-hook-form'
import { WorkflowProcessType } from '../../types/intakeQueue.types'
import { noop } from 'lodash'

const PatientEConsent = ({
  from,
  patId,
  caseID,
  formMethodsRef,
  workflowDef,
}: {
  from: string
  patId?: string
  caseID?: string
  formMethodsRef?: React.MutableRefObject<UseFormReturn<FieldValues> | null>
  workflowDef?: WorkflowProcessType | null
}) => {
  const dispatch = useAppDispatch()
  const { caseId } = useParams()
  const [searchParams] = useSearchParams()
  const case_id = searchParams.get('caseId')
  const patientData = useAppSelector((state) => state?.patientData?.patientData)
  const caseData = useAppSelector((state) => state?.workflowData?.caseData)
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const [isOpenGenerateConsentModal, setIsOpenGenerateConsentModal] =
    useState<boolean>(false)
  const [patientKey, setPatientKey] = useState<string | null>(null)
  const [consentButton, setConsentButton] = useState<boolean>(
    from === CHECK_ELIGIBILITY_LOCATION.ENTITIES
  )
  const [patientId, setPatientId] = useState<number | null>(null)
  const [productId, setProductId] = useState<number | null>(null)
  const [caseProviderKey, setCaseProviderKey] = useState<string | null>(null)
  const openPatientLayoutModal = () => {
    fetchPatientData()
  }
  const closeGenerateConsentModal = () => {
    setIsOpenGenerateConsentModal(false)
  }
  const showError = (message: string) =>
    dispatch(addToaster({ id: uuid(), message, type: 'error' }))

  const ifEligibleToRunEConsent = (
    patientId: string | null,
    patientKey: string | null,
    productId: string | null
  ): boolean => {
    const cleanPatientId = patientId?.toString() ?? ''
    const cleanPatientKey = patientKey?.toString() ?? ''
    const cleanProduct = productId?.toString() ?? ''

    if (
      !cleanPatientId?.length &&
      !cleanProduct?.length &&
      !cleanPatientKey?.length
    ) {
      showError(GENERATE_CONSENT_NAMESPACES.PATIENT_PRODUCT_REQUIRED)
      return false
    }

    if (!cleanPatientId?.length || !cleanPatientKey?.length) {
      showError(GENERATE_CONSENT_NAMESPACES.PATIENT_REQUIRED)
      return false
    }

    if (!cleanProduct?.length) {
      showError(GENERATE_CONSENT_NAMESPACES.PRODUCT_REQUIRED)
      return false
    }

    return true
  }
  const fetchPatientData = async () => {
    try {
      switch (from) {
        case CHECK_ELIGIBILITY_LOCATION.ENTITIES: {
          setPatientKey(patientData?.patient_key)
          setPatientId(Number(patId ?? ''))
          setCaseProviderKey(null)
          setIsOpenGenerateConsentModal(true)
          break
        }
        case CHECK_ELIGIBILITY_LOCATION.INTAKE:
        case CHECK_ELIGIBILITY_LOCATION.CASE: {
          const embeddedFields = ['patient', 'products', 'c_provider']
          const response = await getEntityDataByIdEmbedded(
            workflowDefinition?.global_variables?.entity_name ??
              workflowDef?.global_variables?.entity_name ??
              '',
            Number(
              workflowDefinition?.global_variables?.entity_id ??
                workflowDef?.global_variables?.entity_id ??
                ''
            ),
            embeddedFields,
            Number(caseId ?? case_id ?? caseID ?? '')
          )
          if (response?.data) {
            const formData = formMethodsRef?.current?.getValues()
            const patientKeyfromFormData = formData?.lookupLabels.patient
              ?.split('|')
              ?.map((item: string) => item?.trim())?.[0]
            const patientKeyVal =
              response?.data?.patient?.patient_key ??
              (patientKeyfromFormData === '--' ? '' : patientKeyfromFormData) ??
              ''
            const patientIdVal =
              response?.data?.patient?.id ?? formData?.patient?.id ?? ''
            const productIdVal =
              formData?.products?.id ?? response?.data?.products?.id ?? ''
            const providerKeyVal =
              response?.data?.c_provider?.provider_key ?? ''
            if (
              ifEligibleToRunEConsent(patientIdVal, patientKeyVal, productIdVal)
            ) {
              setProductId(productIdVal)
              setPatientId(Number(patientIdVal))
              setPatientKey(patientKeyVal)
              setCaseProviderKey(providerKeyVal || null)
              setIsOpenGenerateConsentModal(true)
            }
          }
          break
        }
      }
    } catch (e) {
      console.error(e)
    }
  }
  const programId =
    caseData?.programs?.id?.toString() ??
    workflowDefinition?.program_id?.toString() ??
    workflowDef?.program_id?.toString() ??
    ''

  const fetchRecipientEntityTypeData = async (programId: string) => {
    try {
      dispatch(showLoader())
      const response = await getEconsentButtonStatus(programId)
      if (response?.data === true) {
        setConsentButton(true)
      }
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  useEffect(() => {
    if (programId && from !== CHECK_ELIGIBILITY_LOCATION.ENTITIES)
      fetchRecipientEntityTypeData(programId ?? '')
  }, [])
  return (
    <div>
      <Tooltip position="top" content={NAMESPACES.E_CONSENT_TOOLTIP}>
        <SvgEConsent
          className={`${
            consentButton ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
          }`}
          onClick={consentButton ? openPatientLayoutModal : noop}
        />
      </Tooltip>
      {isOpenGenerateConsentModal && (
        <AddDataModal>
          <Modal
            isOpen={isOpenGenerateConsentModal}
            closeModal={closeGenerateConsentModal}
            closeModalOnOutsideClick={false}
            showCloseButton={false}
            width="min-w-[75vw] max-w-[60%] h-[40rem]"
          >
            <GenerateEConsentModalContent
              workflowDef={workflowDef}
              from={from}
              closeGenerateConsentModal={closeGenerateConsentModal}
              patientKey={patientKey}
              patientId={patientId}
              productId={productId}
              caseID={caseID}
              caseProviderKey={caseProviderKey}
            />
          </Modal>
        </AddDataModal>
      )}
    </div>
  )
}

export default PatientEConsent
